<template>
    <div>
    <v-card
        class="mx-auto mt-4"
        max-width="98%"
        flat
        color="transparent"
    >
    <v-toolbar
        color="#37474F"
        dark
        rounded="lg"
        class="mb-6 elevation-4 flex-wrap"
        style="height: auto;"
    >    
        <v-toolbar-title class="font-weight-bold">
            <v-icon left>mdi-shield-check</v-icon>
            유저 이벤트 검수
        </v-toolbar-title>
        <v-spacer/>

        <v-select
            v-model="reviewTypeFilter"
            :items="reviewTypeFilterOptions"
            item-text="text"
            item-value="value"
            dark
            dense
            hide-details
            outlined
            prepend-inner-icon="mdi-filter-variant"
            class="review-type-select mr-4"
            style="max-width: 160px;"
            @change="LoadList"
        ></v-select>

        <v-btn-toggle
            v-model="viewMode"
            mandatory
            class="mode-toggle mr-4 elevation-2 flex-wrap"
            @change="LoadList"
            dense
            active-class="active-mode"
        >
            <v-btn value="not_verify" dark>
                <v-icon left small>mdi-filter-variant</v-icon>
                미인증 내역
            </v-btn>
            <v-btn value="all" dark>
                <v-icon left small>mdi-format-list-bulleted</v-icon>
                전체 내역
            </v-btn>
        </v-btn-toggle>

        <v-divider vertical class="mx-2" inset></v-divider>

        <div class="d-flex align-center ml-2 flex-wrap">
            <v-checkbox
                v-if="viewMode === 'not_verify'"
                v-model="selectAll"
                label="전체 선택"
                hide-details
                dark
                class="mt-0 mr-4"
                @change="ToggleAll"
            ></v-checkbox>
            <v-btn
                v-if="viewMode === 'not_verify'"
                color="success"
                class="mr-2"
                @click="ProcessSelection('Y')"
                :disabled="selectedPids.length === 0"
                elevation="2"
            >
                선택 인증 ({{ selectedPids.length }})
            </v-btn>
            <v-btn
                v-if="viewMode === 'not_verify'"
                color="error"
                @click="ProcessSelection('R')"
                :disabled="selectedPids.length === 0"
                elevation="2"
            >
                선택 반려
            </v-btn>
        </div>

        <v-divider vertical class="mx-2" inset></v-divider>

        <v-btn
            color="cyan"
            dark
            small
            @click="openCreateDialog"
            elevation="2"
        >
            <v-icon left small>mdi-pencil-plus</v-icon>
            게시글 작성
        </v-btn>
    </v-toolbar>

    <div v-if="loadingData" class="text-center py-10">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>

    <v-row v-else-if="notice_list.length > 0">
        <v-col
            v-for="item in notice_list"
            :key="item.PID"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            xl="2"
        >
            <v-card
                class="notice-card elevation-2 mb-4"
                :class="{ 
                    'selected-card': IsSelected(item.PID) && viewMode === 'not_verify',
                    'readonly-card': viewMode !== 'not_verify'
                }"
                @click="viewMode === 'not_verify' ? ToggleSelect(item.PID) : null"
            >
                <!-- Image Section with Zoom Effect -->
                <v-hover v-slot="{ hover }">
                    <div class="image-container">
                        <v-img
                            :src="GetItemImage(item)"
                            height="200"
                            class="grey lighten-2"
                            contain
                        >
                            <template v-slot:placeholder>
                                <v-row class="fill-height ma-0" align="center" justify="center">
                                    <v-icon large color="grey lighten-4">mdi-image-off</v-icon>
                                </v-row>
                            </template>
                            
                            <!-- Selection Overlay -->
                            <v-fade-transition>
                                <div
                                    v-if="IsSelected(item.PID) && viewMode === 'not_verify'"
                                    class="selection-overlay d-flex align-center justify-center text-h2 white--text"
                                >
                                    <v-icon color="white" x-large>mdi-check-circle</v-icon>
                                </div>
                            </v-fade-transition>
                        </v-img>

                        <!-- Zoomed Preview on Hover -->
                        <div v-if="hover" class="zoom-preview shadow-lg">
                            <v-img
                                :src="GetItemImage(item)"
                                min-width="400"
                                max-width="800"
                                max-height="600"
                                class="white"
                                contain
                                elevation="12"
                            ></v-img>
                        </div>
                    </div>
                </v-hover>

                <v-card-text class="pt-4 pb-2">
                    <div class="d-flex justify-space-between align-start mb-1">
                        <div class="text-subtitle-1 font-weight-bold text-truncate" style="max-width: 70%">
                            {{ item.TITLE || UserLabel(item) }}
                        </div>
                        <v-chip
                            :color="GetStatusColor(item.VERIFIED)"
                            dark
                            x-small
                            class="font-weight-bold"
                        >
                            {{ GetStatusText(item.VERIFIED) }}
                        </v-chip>
                    </div>

                    <div class="mb-2">
                        <v-chip
                            :color="GetReviewTypeColor(item.REVIEW_TYPE)"
                            x-small
                            outlined
                            class="font-weight-bold"
                        >
                            <v-icon x-small left>{{ item.REVIEW_TYPE === 'MARKET' ? 'mdi-storefront' : 'mdi-widgets' }}</v-icon>
                            {{ GetReviewTypeText(item.REVIEW_TYPE) }}
                        </v-chip>
                    </div>

                    <div class="text-caption grey--text text--darken-1 d-flex align-center">
                        <v-icon x-small class="mr-1">mdi-account</v-icon> {{ UserLabel(item) }}
                        <v-spacer></v-spacer>
                        <v-icon x-small class="mr-1">mdi-clock-outline</v-icon> {{ FormatDate(item.CREATE_DT) }}
                    </div>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-2">
                    <v-btn 
                        text 
                        small 
                        color="primary"
                        @click.stop="ViewDetail(item)"
                    >
                        상세보기
                    </v-btn>
                    <v-spacer></v-spacer>
                    <template v-if="IsPending(item.VERIFIED)">
                        <v-btn
                            color="success"
                            small
                            depressed
                            @click.stop="ProcessItem(item.PID, 'Y')"
                        >
                            인증
                        </v-btn>
                        <v-btn
                            color="error"
                            small
                            depressed
                            @click.stop="ProcessItem(item.PID, 'R')"
                        >
                            반려
                        </v-btn>
                    </template>
                </v-card-actions>
            </v-card>
        </v-col>
    </v-row>

    <v-row v-else class="fill-height py-16" align="center" justify="center">
        <v-col cols="12" class="text-center">
            <v-icon size="100" color="grey lighten-1">mdi-folder-open-outline</v-icon>
            <div class="text-h6 grey--text mt-4">항목이 없습니다.</div>
        </v-col>
    </v-row>
   
    </v-card>

    <!-- Detail Dialog -->
    <v-dialog v-model="detailDialog" max-width="900" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card v-if="selectedDetailItem">
            <v-card-title class="blue-grey darken-4 white--text">
                {{ selectedDetailItem.TITLE || UserLabel(selectedDetailItem) }}
                <v-chip
                    :color="GetReviewTypeColor(selectedDetailItem.REVIEW_TYPE)"
                    x-small
                    class="ml-3 font-weight-bold"
                >
                    {{ GetReviewTypeText(selectedDetailItem.REVIEW_TYPE) }}
                </v-chip>
                <v-spacer></v-spacer>
                <v-btn icon dark @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-card-text class="pa-6">
                <div class="d-flex mb-4 text-subtitle-2 grey--text">
                    <span>작성자: {{ UserLabel(selectedDetailItem) }}</span>
                    <v-divider vertical class="mx-3"></v-divider>
                    <span>작성일: {{ FormatDate(selectedDetailItem.CREATE_DT) }}</span>
                </div>
                <!-- Review image -->
                <v-img
                    :src="GetItemImage(selectedDetailItem)"
                    max-height="400"
                    contain
                    class="grey lighten-3 rounded mb-4"
                ></v-img>
                <!-- Content rendering -->
                <div v-if="selectedDetailItem.MESSAGE" class="q-content-box" v-html="selectedDetailItem.MESSAGE"></div>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-4" v-if="IsPending(selectedDetailItem.VERIFIED)">
                <v-btn color="success" block large @click="ProcessItem(selectedDetailItem.PID, 'Y')">이 게시글 인증하기</v-btn>
                <v-btn
                    color="error"
                    class="ml-2"
                    large
                    @click="ProcessItem(selectedDetailItem.PID, 'R')"
                >
                    반려
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Reject Reason Dialog -->
    <v-dialog v-model="rejectDialog" max-width="500" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card>
            <v-card-title class="error white--text">
                <v-icon left dark>mdi-alert-circle</v-icon>
                반려 사유 입력
            </v-card-title>
            <v-card-text class="pt-6">
                <v-textarea
                    v-model="rejectReason"
                    label="반려 사유를 입력해주세요"
                    outlined
                    rows="4"
                    placeholder="예: 이미지가 불분명함, 도용된 이미지 등"
                    hide-details
                ></v-textarea>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn text @click="rejectDialog = false">취소</v-btn>
                <v-btn color="error" class="px-6" @click="ExecuteReject" :disabled="!rejectReason.trim()">
                    반려 처리
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      timeout="3000"
      top
    >
      {{ snackbarText }}
    </v-snackbar>

    <!-- 게시글 작성 다이얼로그 -->
    <v-dialog v-model="createDialog" max-width="600" scrollable>
      <v-card>
        <v-card-title>
          <v-icon left color="cyan">mdi-pencil-plus</v-icon>
          유저 리뷰 게시글 작성
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-select
            v-model="createForm.REVIEW_TYPE"
            :items="reviewTypeOptions"
            item-text="text"
            item-value="value"
            label="리뷰 타입 *"
            outlined dense hide-details class="mb-3"
            prepend-inner-icon="mdi-filter-variant"
          ></v-select>
          <v-text-field
            v-model="createForm.USER_ID"
            label="유저 아이디 *"
            outlined dense hide-details class="mb-3"
          ></v-text-field>
          <v-select
            v-model="createForm.PRODUCT_PID"
            :items="productOptions"
            item-text="label"
            item-value="PID"
            label="현금 상품 선택 (선택)"
            outlined dense hide-details clearable class="mb-3"
            :loading="productLoading"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.COIN_NM || item.COIN_TAG }} <span class="grey--text text-caption ml-1">(PID: {{ item.PID }})</span></v-list-item-title>
                <v-list-item-subtitle>{{ item.PRODUCT_PRICE }}원 / {{ item.ENABLE_STATE === 'Y' ? '활성' : '비활성' }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-select>
          <v-text-field
            v-model="createForm.TITLE"
            label="제목 *"
            outlined dense hide-details class="mb-3"
          ></v-text-field>
          <v-textarea
            v-model="createForm.MESSAGE"
            label="내용"
            outlined dense hide-details class="mb-3"
            rows="3"
          ></v-textarea>
          <v-file-input
            v-model="createImageFile"
            label="이미지 첨부"
            outlined dense hide-details class="mb-3"
            accept="image/*"
            prepend-icon="mdi-camera"
            @change="onCreateImageChange"
          ></v-file-input>
          <v-img v-if="createImagePreview" :src="createImagePreview" max-height="160" contain class="mb-3 grey lighten-4 rounded"></v-img>
          <v-row dense>
            <v-col cols="6">
              <v-select
                v-model="createForm.NOTICE_TYPE"
                :items="noticeTypeOptions"
                label="타입"
                outlined dense hide-details class="mb-3"
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="createForm.ENABLE_REPLY"
                :items="enableReplyOptions"
                label="댓글"
                outlined dense hide-details class="mb-3"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="createDialog = false">취소</v-btn>
          <v-btn color="primary" depressed @click="submitCreate" :loading="createLoading">
            <v-icon left small>mdi-check</v-icon>등록
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    </div>

</template>


<script>

import { mapActions } from 'vuex';
import moment from 'moment'
const { FileUpload_Async } = require("../../util/fileutil");

export default {
    name: 'Game_Notice_GoldVerify',

    data: () => ({
        loadingData: false,
        viewMode: 'not_verify',
        selectedPids: [],
        selectAll: false,
        notice_list: [],

        // 리뷰 타입 필터 (금리뷰=GOLD → PUBLIC_NOTICE_VERIFY / 마켓리뷰=MARKET → PUB_REVIEW_VERIFY)
        reviewTypeFilter: 'ALL',
        reviewTypeFilterOptions: [
            { text: '전체', value: 'ALL' },
            { text: '금리뷰', value: 'GOLD' },
            { text: '마켓리뷰', value: 'MARKET' },
        ],

        detailDialog: false,
        selectedDetailItem: null,

        rejectDialog: false,
        rejectReason: '',
        rejectTargetPids: '',

        // 게시글 작성
        createDialog: false,
        createLoading: false,
        createImageFile: null,
        createImagePreview: '',
        productOptions: [],
        productLoading: false,
        createForm: {
            USER_ID: '',
            TITLE: '',
            MESSAGE: '',
            IMAGE: '',
            PRODUCT_PID: '',
            NOTICE_TYPE: 'N',
            ENABLE_REPLY: 'Y',
            REVIEW_TYPE: 'GOLD',
        },
        reviewTypeOptions: [
            { text: '금리뷰 (GOLD)', value: 'GOLD' },
            { text: '마켓리뷰 (MARKET)', value: 'MARKET' },
        ],
        noticeTypeOptions: [
            { text: '일반', value: 'N' },
            { text: '히든 (FAQ 등)', value: 'H' },
        ],
        enableReplyOptions: [
            { text: '허용', value: 'Y' },
            { text: '비허용', value: 'N' },
        ],

        // UI Helpers
        snackbar: false,
        snackbarText: '',
        snackbarColor: 'success',
    }),

    created()
    {    
        this.LoadList();    
    },

    methods:{
        ...mapActions([
            'CARRYGOLD_GET_NOTICE_LIST_NOT_VERIFY',
            'CARRYGOLD_GET_NOTICE_LIST_VERIFY_ALL',
            'CARRYGOLD_SET_NOTICE_LIST_VERIFY',
            'CARRYGOLD_CREATE_NOTICE_VERIFY',
            'CARRYGOLD_GET_SWAP_COIN_LIST'
        ]),
        
        async LoadList(){   
            this.loadingData = true;
            this.selectedPids = [];
            this.selectAll = false;
            try {
                let body_data;
                const payload = { review_type: this.reviewTypeFilter };
                if (this.viewMode === 'not_verify') {
                    body_data = await this.CARRYGOLD_GET_NOTICE_LIST_NOT_VERIFY(payload);
                } else {
                    body_data = await this.CARRYGOLD_GET_NOTICE_LIST_VERIFY_ALL(payload);
                }

                this.notice_list = (body_data && body_data.notice_list) || [];
                console.log("Loaded List:", this.notice_list);
            } catch (err) {
                console.error(err);
                this.ShowSnackbar("데이터를 불러오는데 실패했습니다.", "error");
            } finally {
                this.loadingData = false;
            }
        },

        GetItemImage(item) {
            const path = item.IMAGE || item.IMAGE_URL;
            if (path) {
                if (path.startsWith('http')) return path;
                return 'https://d1r6v24dyewnnj.cloudfront.net/' + path;
            }
            // Fallback to extract from message if no direct image property
            const match = item.MESSAGE ? item.MESSAGE.match(/<img[^>]+src="([^">]+)"/) : null;
            return match ? match[1] : '';
        },

        FormatDate(dateStr) {
            if (!dateStr) return '';
            return moment(dateStr).format('YYYY-MM-DD HH:mm');
        },

        ToggleSelect(pid) {
            const index = this.selectedPids.indexOf(pid);
            if (index > -1) {
                this.selectedPids.splice(index, 1);
            } else {
                this.selectedPids.push(pid);
            }
            this.selectAll = this.selectedPids.length === this.notice_list.length && this.notice_list.length > 0;
        },

        IsSelected(pid) {
            return this.selectedPids.includes(pid);
        },

        ToggleAll() {
            if (this.selectAll) {
                this.selectedPids = this.notice_list.map(item => item.PID);
            } else {
                this.selectedPids = [];
            }
        },

        ViewDetail(item) {
            this.selectedDetailItem = item;
            this.detailDialog = true;
        },

        async ProcessSelection(state) {
            const pids = this.selectedPids.join(',');
            if (state === 'R') {
                this.OpenRejectDialog(pids);
            } else {
                await this.ExecuteVerification(pids, state);
            }
        },

        async ProcessItem(pid, state) {
            if (state === 'R') {
                this.OpenRejectDialog(pid.toString());
            } else {
                await this.ExecuteVerification(pid, state);
            }
        },

        OpenRejectDialog(pids) {
            this.rejectTargetPids = pids;
            this.rejectReason = '리뷰 이미지가 요건에 맞지 않습니다.';
            this.rejectDialog = true;
        },

        async ExecuteReject() {
            if (!this.rejectReason.trim()) return;
            await this.ExecuteVerification(this.rejectTargetPids, 'R', this.rejectReason);
            this.rejectDialog = false;
        },

        // 금리뷰(GOLD)와 마켓리뷰(MARKET)는 저장 테이블이 달라 PID 가 겹칠 수 있다.
        // 따라서 선택 항목을 타입별로 묶어 타입마다 따로 호출한다.
        GroupPidsByReviewType(pids) {
            const pidList = String(pids).split(',').map(s => s.trim()).filter(Boolean);
            const groups = {};
            for (const pid of pidList) {
                const item = this.notice_list.find(it => String(it.PID) === pid);
                const type = (item && item.REVIEW_TYPE) || 'GOLD';
                (groups[type] = groups[type] || []).push(pid);
            }
            return groups;
        },

        async ExecuteVerification(pids, verified_state, reason = '') {
            if (verified_state === 'Y' && !confirm("선택한 항목을 인증하시겠습니까?")) {
                return;
            }

            const groups = this.GroupPidsByReviewType(pids);
            const types = Object.keys(groups);
            if (types.length === 0) return;

            this.loadingData = true;
            try {
                for (const review_type of types) {
                    const result = await this.CARRYGOLD_SET_NOTICE_LIST_VERIFY({
                        pids: groups[review_type].join(','),
                        verified_state,
                        reason,
                        review_type,
                    });
                    console.log(`Verification Result (${review_type}):`, result);
                }
                this.ShowSnackbar("처리가 완료되었습니다.", "success");
                this.detailDialog = false;
                await this.LoadList();
                this.$root.$emit('menu-dot:refresh');   // 메뉴 레드닷 갱신
            } catch (err) {
                console.error(err);
                this.ShowSnackbar("처리 중 오류가 발생했습니다.", "error");
            } finally {
                this.loadingData = false;
            }
        },

        // 유저 표시 통일: 닉네임(USER_ID). 닉네임 없으면 USER_ID 만.
        UserLabel(item) {
            if (!item) return '';
            const nick = item.USER_NICK || '';
            const uid = item.USER_ID || item.CREATE_USER || '';
            if (nick && uid) return `${nick}(${uid})`;
            return nick || uid || '-';
        },

        GetStatusColor(status) {
            if (status === 'Y') return 'success';
            if (status === 'R' || status === 'C') return 'error';
            return 'warning';
        },

        GetStatusText(status) {
            if (status === 'Y') return '인증완료';
            if (status === 'R' || status === 'C') return '반려됨';
            return '대기중';
        },

        // 미인증(대기) 상태: N(신규/기본) 또는 W(대기)
        IsPending(status) {
            return status === 'N' || status === 'W';
        },

        GetReviewTypeText(type) {
            if (type === 'MARKET') return '마켓리뷰';
            return '금리뷰';
        },

        GetReviewTypeColor(type) {
            if (type === 'MARKET') return 'deep-purple';
            return 'amber darken-2';
        },

        ShowSnackbar(text, color) {
            this.snackbarText = text;
            this.snackbarColor = color || 'success';
            this.snackbar = true;
        },

        async openCreateDialog() {
            this.createForm = {
                USER_ID: '',
                TITLE: '',
                MESSAGE: '',
                IMAGE: '',
                PRODUCT_PID: '',
                NOTICE_TYPE: 'N',
                ENABLE_REPLY: 'Y',
                // 관리자 게시글 작성은 게시글형(GOLD) 전용 — 마켓리뷰는 유저 앱에서만 생성된다
                REVIEW_TYPE: 'GOLD',
            };
            this.createImageFile = null;
            this.createImagePreview = '';
            this.createDialog = true;
            // 상품 리스트 로드
            if (this.productOptions.length === 0) {
                this.productLoading = true;
                try {
                    const body_data = await this.CARRYGOLD_GET_SWAP_COIN_LIST({});
                    this.productOptions = (body_data.coin_list || []).map(item => ({
                        ...item,
                        label: `${item.COIN_NM || item.COIN_TAG} (${item.PRODUCT_PRICE}원)`
                    }));
                } catch (err) {
                    console.error('상품 리스트 로드 실패:', err);
                } finally {
                    this.productLoading = false;
                }
            }
        },

        onCreateImageChange(file) {
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => { this.createImagePreview = e.target.result; };
                reader.readAsDataURL(file);
            } else {
                this.createImagePreview = '';
            }
        },

        async submitCreate() {
            if (!this.createForm.USER_ID || !this.createForm.TITLE || !this.createForm.REVIEW_TYPE) {
                alert('유저 아이디, 제목, 리뷰 타입은 필수입니다.');
                return;
            }
            this.createLoading = true;
            try {
                // 이미지 업로드
                if (this.createImageFile) {
                    const timestamp = Date.now();
                    const ext = this.createImageFile.name.split('.').pop();
                    const uploadPath = `carrygold/notice_verify/images/${timestamp}.${ext}`;
                    await FileUpload_Async(this.createImageFile, uploadPath);
                    this.createForm.IMAGE = uploadPath;
                }
                await this.CARRYGOLD_CREATE_NOTICE_VERIFY(this.createForm);
                this.ShowSnackbar('게시글이 등록되었습니다.', 'success');
                this.createDialog = false;
                await this.LoadList();
            } catch (err) {
                console.error(err);
                this.ShowSnackbar('게시글 등록에 실패했습니다.', 'error');
            } finally {
                this.createLoading = false;
            }
        },
    },

    watch :{
        '$store.state.selected_game_package': function() {
            this.LoadList();
        },
    }
};
</script>

<style scoped>
.mode-toggle {
    background-color: rgba(0, 0, 0, 0.2) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 8px !important;
    overflow: hidden;
}

.active-mode {
    background-color: #FB8C00 !important; /* Orange for active */
    color: white !important;
    font-weight: bold !important;
}

.active-mode .v-icon {
    color: white !important;
}

.notice-card {
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    border: 2px solid transparent !important;
}

.notice-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.15) !important;
}

.selected-card {
    border-color: #0277BD !important;
    background-color: #E1F5FE !important;
}

.readonly-card {
    cursor: default !important;
}

.readonly-card:hover {
    transform: none !important;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
}

.image-container {
    position: relative;
    background-color: #f5f5f5;
}

.selection-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(1, 87, 155, 0.4);
}

.zoom-preview {
    position: absolute;
    z-index: 999;
    top: 0;
    left: 100%;
    margin-left: 10px;
}

@media (max-width: 960px) {
    .zoom-preview {
        left: 0;
        top: 100%;
        margin-left: 0;
        margin-top: 10px;
    }
}

.q-content-box {
    border: 1px solid #efefef;
    padding: 20px;
    border-radius: 8px;
    background-color: #fafafa;
}

.q-content-box img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 20px auto;
    border-radius: 4px;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}
</style>
